<template>
    <Dropdown
        class="upgrade-popup"
        arrow
        :align="align"
        :hoverable="hoverable && !($browser && $browser.is && $browser.is.touch)"
        :boundary="boundary"
        :position="position"
        :teleport="teleport"
        :disabled="hasPlan || disabled"
        :class="{ left, right }"
        fullscreen
        @open="() => {
            if (hoverable) return;
            $track.event('Upgrade Triggered', {
                context,
                'trigger_context': context
            });
        }"
        @hover="event => {
            if (!hoverable) return;
            $emit('hover', event);
            if (isHovering && event) return;
            if (hoverable) isHovering = event;
            if (!event) return;
            $track.event('Upgrade Triggered', {
                context,
                'trigger_context': context
            });
        }"
    >
        <template #element="{ toggle }">
            <Clickable v-if="!hasPlan && !disabled" @click="toggle($event); $emit('click', $event)">
                <slot :toggle="toggle">
                    <Chip
                        variant="fade-primary"
                        icon="lightning"
                        :text="{ size: 'marker', heading: false }"
                        left
                    >
                        Upgrade
                    </Chip>
                </slot>
            </Clickable>
            <slot v-else>
                <span />
            </slot>
        </template>
        <template #menu>
            <div class="upgrade-header">
                <Icon :name="upgradeReason.icon" class="anchor" />
                <Txt size="xs" heading>
                    {{ upgradeReason.title }}
                </Txt>
                <Chip
                    variant="fade-primary"
                    icon="lightning"
                    :text="{ size: 'marker', heading: false }"
                >
                    Upgrade
                </Chip>
            </div>
            <Txt size="m">
                {{ upgradeReason.description }}
            </Txt>
            <template v-if="reason === 'payment-fees'">
                <Txt size="s" flex>
                    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.68058 2.48429C7.78844 2.17516 8.21155 2.17516 8.31942 2.48429L9.7342 6.53885C9.76781 6.63515 9.84045 6.71149 9.93308 6.74784L13.781 8.25779C14.073 8.37237 14.073 8.79918 13.781 8.91376L9.93308 10.4237C9.84045 10.4601 9.76781 10.5364 9.7342 10.6327L8.31942 14.6873C8.21155 14.9964 7.78844 14.9964 7.68058 14.6873L6.26579 10.6327C6.23219 10.5364 6.15955 10.4601 6.06692 10.4237L2.219 8.91376C1.927 8.79918 1.927 8.37237 2.219 8.25779L6.06692 6.74784C6.15955 6.71149 6.23219 6.63515 6.26579 6.53885L7.68058 2.48429Z" fill="#446CFC" />
                    </svg>
                    <strong>Team</strong>
                    2% application fee
                </Txt>
                <Txt size="s" flex>
                    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.6888 2.49271C7.77662 2.17235 8.22305 2.17235 8.31087 2.49271L9.24078 5.885C9.28798 6.05719 9.46185 6.15938 9.63204 6.11497L12.9286 5.25471C13.2423 5.17285 13.4659 5.55939 13.2433 5.79873L10.8616 8.35999C10.7438 8.48666 10.7438 8.68489 10.8616 8.81156L13.2433 11.3728C13.4659 11.6122 13.2423 11.9987 12.9286 11.9168L9.63204 11.0566C9.46185 11.0122 9.28798 11.1144 9.24078 11.2866L8.31087 14.6788C8.22305 14.9992 7.77662 14.9992 7.6888 14.6788L6.75889 11.2866C6.71169 11.1144 6.53783 11.0122 6.36763 11.0566L3.07112 11.9168C2.75742 11.9987 2.53376 11.6122 2.75633 11.3728L5.13808 8.81156C5.25587 8.68489 5.25587 8.48666 5.13808 8.35999L2.75633 5.79873C2.53376 5.55939 2.75742 5.17285 3.07112 5.25471L6.36763 6.11497C6.53783 6.15938 6.71169 6.05719 6.75889 5.885L7.6888 2.49271Z" fill="#446CFC" />
                    </svg>
                    <strong>Business</strong>
                    1% application fee
                </Txt>
            </template>
            <Txt v-else-if="!upgradeReason.poa" size="s" class="text-primary">
                Available on
                <span
                    class="plans"
                    :class="{ multiple: upgradeReason.plan.length > 1 }"
                    v-html="plansFormatted"
                />
                {{ upgradeReason.plan.length > 1 ? 'plans' : 'plan' }}
            </Txt>
            <div v-if="!upgradeReason.poa" class="upgrade-actions">
                <Button
                    colour="smoke"
                    size="xxxs" heading
                    href="/pricing"
                    slim
                    :data-context="context"
                >
                    Compare plans
                </Button>
                <Button
                    v-if="upgradeUrl"
                    size="xxxs" heading
                    :href="upgradeUrl"
                    slim
                    @click.stop="$track.event('Subscription Upgrade Started', {
                        'context': context,
                        'trigger_context': context
                    })"
                >
                    Upgrade
                </Button>
            </div>
            <div v-else class="upgrade-actions">
                <Button
                    data-intercom-target="sales"
                    size="xxs" heading
                    slim
                    @click="$emit('close')"
                >
                    Talk to us
                </Button>
            </div>
        </template>
    </Dropdown>
</template>

<script>
import usePrices from '@/store/prices';
import { useResponsive } from '@/store/responsive';

import Txt from '@/components/form/txt';
import Chip from '@/components/utility/chip';
import Icon from '@/components/utility/icon';
import Button from '@/components/utility/button';
import Dropdown from '@/components/utility/dropdown';
import Clickable from '@/components/utility/clickable';

export default  {
    name: 'UpgradePopup',
    components: { Dropdown, Chip, Clickable, Txt, Button, Icon },
    inject: ['$global', '$browser'],
    props: {
        reason: { type: String, default: null },
        left: { type: Boolean, default: false },
        right: { type: Boolean, default: false },
        align: { type: String, default: 'center' },
        position: { type: String, default: 'bottom' },
        teleport: { type: Boolean, default: false },
        context: { type: String, default: null },

        hoverable: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        boundary: { type: HTMLElement, default: undefined }
    },
    emits: ['upgrade', 'cancel', 'close', 'click', 'hover'],
    data() {
        return {
            isHovering: false,
            upgradeUrl: null,
            viewport: useResponsive()
        };
    },
    computed: {
        hasPlan() {
            if (!this.$access.isAuthor) return true;
            if (!this.upgradeReason) return false;
            if (this.upgradeReason.poa) return false;
            return this.$global && this.$global.hasPlan && this.$global.hasPlan(this.upgradeReason.plan[0]);
        },
        upgradeReasons() {
            return [
                {
                    id: 'folders',
                    icon: 'folder',
                    title: 'Course folders',
                    description: 'Clean up your dashboard by organizing your courses into folders.',
                    plan: ['team','business']
                },
                {
                    id: 'team',
                    icon: 'creator',
                    title: 'Invite more creators',
                    description: 'Invite multiple creators to create and share courses in your workspace.',
                    plan: ['team','business']
                },
                {
                    id: 'team-plus',
                    icon: 'creator',
                    title: 'Invite more creators',
                    description: 'Invite multiple creators to create and share courses in your workspace.',
                    plan: ['business']
                },
                {
                    id: 'embiggen',
                    icon: 'creator',
                    title: 'Invite more creators',
                    description: '10 creators not enough? Talk to us about how we can customize a plan to suit your needs.',
                    poa: true
                },
                {
                    id: 'logo',
                    icon: 'logo',
                    title: 'Custom branding',
                    description: 'Brand your courses with custom logos displayed on each screen.',
                    plan: ['business']
                },
                {
                    id: 'logoAdded',
                    icon: 'logo',
                    title: 'Custom branding',
                    description: 'We’ve added your logo to this course so you can see your brand in action. To update it or apply branding to more courses, upgrade to the Business plan.',
                    plan: ['business']
                },
                {
                    id: 'branding',
                    icon: 'accounts',
                    title: 'Remove branding',
                    description: 'Remove all Coassemble branding from courses.',
                    plan: ['solo', 'team', 'business']
                },
                {
                    id: 'scorm',
                    icon: 'checklist',
                    title: 'Multiple domains',
                    description: 'Host your SCORM packages in multiple locations with an unrestricted SCORM allowlist and additional security options. ',
                    plan: ['business']
                },
                {
                    id: 'access',
                    icon: 'key',
                    title: 'Advanced access controls',
                    description: `Choose who can view your course, or request viewers to identify themselves before getting started. 
                    
                    Protect your IP by choosing what domains can host your SCORM packages.`,
                },
                {
                    id: 'insights',
                    icon: 'results-b',
                    title: 'Individual insights',
                    description: `Get detailed insights on unique usage and host locations for all shared SCORM files.`,
                    plan: ['team', 'business']
                },
                {
                    id: 'insights-scorm',
                    icon: 'results-b',
                    title: 'SCORM usage report',
                    description: `Explore detailed insights into each individual viewer's course experience.`,
                    plan: ['business']
                },
                {
                    id: 'headless',
                    icon: 'headless',
                    title: 'Headless',
                    description: 'If you’re ready to deploy your integration to production, and want to remove all test mode restrictions from Headless, then you’ll need to change your plan.',
                    plan: ['business']
                },
                {
                    id: 'payment-links',
                    icon: 'price',
                    title: 'Payment links',
                    description: 'If you’re ready to start selling your courses, and want to remove all test mode restrictions then you’ll need to upgrade your plan.',
                    plan: ['solo', 'team', 'business']
                },
                {
                    id: 'payment-fees',
                    icon: 'price',
                    title: 'Reduced fees',
                    description: 'Reduce the Cossemble application fee made on course purchases by upgrading to one of the following plans:',
                    plan: ['business']
                },
                {
                    id: 'assets',
                    icon: 'image-multiple',
                    title: 'Image library',
                    description: 'Find and manage image assets in one place, including saving images from converted documents.',
                },
                {
                    id: 'quiz',
                    icon: 'correct-circle-outline',
                    title: 'Quiz scoring',
                    description: 'Set a passing score and allow multiple quiz attempts to ensure a comprehensive understanding of the course material.',
                    plan: ['solo', 'team', 'business']
                },
                {
                    id: 'video',
                    icon: 'video',
                    title: 'Enforce completion',
                    description: 'Force your viewer to watch the entire video before they can progress to the next screen.',
                    plan: ['team', 'business']
                },
                {
                    id: 'video-upload',
                    icon: 'video',
                    title: 'Video upload',
                    description: 'Upload your own videos to use in Coassemble courses.',
                    plan: ['team', 'business']
                },
                {
                    id: 'collections',
                    icon: 'catalog',
                    title: 'Collections',
                    description: 'Curate a collection of courses to give your viewers easy access to multiple courses all in one convenient location.',
                    plan: ['business']
                }
            ];
        },
        upgradeReason() {
            return this.upgradeReasons.find(r => r.id === this.reason);
        },
        plansFormatted() {
            if (!this.upgradeReason) return '';
            const availablePlans = this.upgradeReason.plan.filter(plan => plan !== 'solo');
            const plans = availablePlans.map(plan => this.getPlanName(plan));
            const lastPlan = plans.pop();
            return plans.length ? plans.join(', ') + ' and ' + lastPlan : lastPlan;
        }
    },
    watch: {
        '$global.isLoggedIn'() {
            this.getUpgradeUrl();
        },
        disabled(to) {
            if (!to) this.getUpgradeUrl();
        }
    },
    mounted() {
        this.getUpgradeUrl();
    },
    methods: {
        getUpgradeUrl() {
            if (!this.$global.isLoggedIn || this.disabled) return;
            usePrices.getUpgradeUrl(this.$http).then((url) => {
                this.upgradeUrl = url;
            });
        },
        getPlanName(plan) {
            return '<strong>' + plan.charAt(0).toUpperCase() + plan.slice(1) + '</strong>';
        }
    }
};
</script>

<style lang="less">
.dropdown.upgrade-popup {
    .clickable {
        flex: 1;
        display: flex;
    }
    &.left { margin-left: 8px; }
    &.right { margin-right: 8px; }
    > .popup > .popup-inner {
        padding: 16px;
        width: 326px;
        text-align: left;
        .upgrade-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
            .txt-component { flex: 1; }
        }
        .plans.multiple {
            margin: 0 2px;
        }
        .upgrade-actions {
            display: flex;
            gap: 8px;
            margin-top: 12px;
            .cobutton { flex: 1 1 50%; }
        }
        .txt-component.body.s.flex {
            &:last-of-type { margin-bottom: 12px; }
            .txt-component-content {
                gap: 4px;
                color: var(--primary);
            }
        }
    }
}
</style>
